import { useModal } from '@billy/design-system'
import find from 'lodash/find'
import React, { memo, useCallback, useState } from 'react'
import { useQueryClient } from 'react-query'

import { Account } from '@modules-deprecated/app/accounts/types'

import { ModalId } from '../../../../../enums/modalId'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { EmberEventFn } from '../../../../../types/emberEventFn'
import { AccountsGroupsSet } from '../types'
import { UpdateAccountFromTemplateModal } from './AccountFromTemplateModal/elements/UpdateAccountFromTemplateModal'
import { ChartOfAccountsListTable } from './ChartOfAccountsListTable'
import { DeleteAccountModal } from './DeleteAccountModal'
import { EditAccountModal } from './EditAccountModal'
import { getEditAccountAllowedFields } from './EditAccountModal/utils/getEditAccountAllowedFields'

interface ChartOfAccountsListProps {
  accountGroupsSet: AccountsGroupsSet
  isLoading: boolean
  onReloadAccounts: EmberEventFn
  shouldCreateAccountFromTemplate: boolean
}

export const ChartOfAccountsList = memo(
  ({ accountGroupsSet, isLoading, onReloadAccounts, shouldCreateAccountFromTemplate }: ChartOfAccountsListProps) => {
    const { groups, accountsInGroups } = accountGroupsSet
    const queryClient = useQueryClient()
    const { open: openAccountFromTemplateModal, close: closeAccountFromTemplateModal } = useModal(
      ModalId.AccountFromTemplateModalEdit,
    )
    const [deletingAccountId, setDeletingAccountId] = useState<string>()
    const [editingAccount, setEditingAccount] = useState<Account>()
    const [isEditingAccountPredefined, setIsEditingAccountPredefined] = useState(false)
    const [isEditAccountModalOpen, setIsEditAccountModalOpen] = useState(false)

    const handleEditAccount = useCallback(
      (account: Account) => {
        if (shouldCreateAccountFromTemplate) {
          setEditingAccount(account)
          openAccountFromTemplateModal()
          return
        }
        setEditingAccount(account)
        setIsEditAccountModalOpen(true)
      },
      [shouldCreateAccountFromTemplate, openAccountFromTemplateModal, setEditingAccount],
    )

    const handleEditPredefinedAccount = useCallback(
      (account: Account) => {
        if (shouldCreateAccountFromTemplate) {
          setEditingAccount(account)
          openAccountFromTemplateModal()
          return
        }
        setIsEditingAccountPredefined(true)
        setEditingAccount(account)
        setIsEditAccountModalOpen(true)
      },
      [shouldCreateAccountFromTemplate, openAccountFromTemplateModal],
    )

    const handleModalSaved = useCallback(() => {
      queryClient.invalidateQueries(QueryKeys.Accounts)
      onReloadAccounts({ detail: null })
    }, [onReloadAccounts, queryClient])

    const handleDelete = useCallback((accountId: string) => {
      setDeletingAccountId(accountId)
    }, [])

    const getAccountGroup = useCallback(
      (account?: Account) => {
        if (!account) {
          return undefined
        }

        const { id } = account

        let foundGroupId: string | undefined

        for (const groupId in accountsInGroups) {
          if (find(accountsInGroups[groupId], ({ id: accId }) => accId === id)) {
            foundGroupId = groupId
            break
          }
        }

        if (!foundGroupId) {
          return undefined
        }

        return find(groups, ({ id }) => foundGroupId === id)
      },
      [accountsInGroups, groups],
    )

    const handleDeleteAccountModalClose = useCallback(() => {
      setDeletingAccountId(undefined)
    }, [])

    const handleEditAccountModalClose = useCallback(() => {
      setIsEditAccountModalOpen(false)
      setIsEditingAccountPredefined(false)
      setEditingAccount(undefined)
    }, [setEditingAccount, setIsEditingAccountPredefined])

    const handleAccountFromTemplateModalClose = useCallback(() => {
      closeAccountFromTemplateModal()
      setEditingAccount(undefined)
    }, [setEditingAccount, closeAccountFromTemplateModal])

    return (
      <>
        <ChartOfAccountsListTable
          accountsInGroups={accountsInGroups}
          groups={groups}
          isLoading={isLoading}
          onEditAccount={handleEditAccount}
          onEditPredefinedAccount={handleEditPredefinedAccount}
          onDeleteAccount={handleDelete}
        />
        <DeleteAccountModal
          accountId={deletingAccountId}
          onClose={handleDeleteAccountModalClose}
          onSaved={handleModalSaved}
        />
        <EditAccountModal
          account={editingAccount}
          allowedFields={
            isEditingAccountPredefined ? getEditAccountAllowedFields(!!editingAccount?.predefinedAccount) : []
          }
          isOpen={isEditAccountModalOpen}
          group={editingAccount ? getAccountGroup(editingAccount) : undefined}
          onClose={handleEditAccountModalClose}
          onSave={handleModalSaved}
        />
        <UpdateAccountFromTemplateModal
          account={editingAccount}
          id={ModalId.AccountFromTemplateModalEdit}
          onClose={handleAccountFromTemplateModalClose}
          onSave={handleModalSaved}
        />
      </>
    )
  },
)
